/**
 * TanStack Query Client Configuration
 * Centralized query client with optimized defaults
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Data considered fresh for 30 seconds
            staleTime: 30 * 1000,

            // Keep unused data in cache for 5 minutes
            gcTime: 5 * 60 * 1000,

            // Retry failed requests once
            retry: 1,

            // Refetch when window regains focus
            refetchOnWindowFocus: true,

            // Refetch on mount if data is stale
            refetchOnMount: true,

            // Refetch on reconnect
            refetchOnReconnect: true,
        },
        mutations: {
            // Retry mutations once on failure
            retry: 1,
        },
    },
});

/**
 * Invalidate all credit-related queries
 * Call this after any operation that affects credits
 */
export const invalidateCreditQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['credits'] });
    queryClient.invalidateQueries({ queryKey: ['creditHistory'] });
    queryClient.invalidateQueries({ queryKey: ['creditAnalytics'] });
    queryClient.invalidateQueries({ queryKey: ['planLimits'] });
};

/**
 * Invalidate session queries
 * Call this after session updates
 */
export const invalidateSessionQueries = (sessionId) => {
    if (sessionId) {
        queryClient.invalidateQueries({ queryKey: ['workflowSession', sessionId] });
    }
    queryClient.invalidateQueries({ queryKey: ['userSessions'] });
    queryClient.invalidateQueries({ queryKey: ['activeSession'] });
};

/**
 * Invalidate user-related queries
 * Call this after auth state changes
 */
export const invalidateUserQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['user'] });
    queryClient.invalidateQueries({ queryKey: ['credits'] });
    queryClient.invalidateQueries({ queryKey: ['userSessions'] });
    queryClient.invalidateQueries({ queryKey: ['planLimits'] });
};

/**
 * Invalidate referral queries
 */
export const invalidateReferralQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['referralData'] });
    queryClient.invalidateQueries({ queryKey: ['referralCode'] });
    queryClient.invalidateQueries({ queryKey: ['referralStats'] });
    queryClient.invalidateQueries({ queryKey: ['referralHistory'] });
};

/**
 * Clear all queries (for logout)
 */
export const clearAllQueries = () => {
    queryClient.clear();
};

export default queryClient;
